import { trackEvent, AnalyticsEvents } from '../analytics/instance';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import { CourseEnrollment, Enrollment } from '../types';

export const useMyEnrollments = () => {
  return useQuery({
    queryKey: ['enrollments', 'my'],
    queryFn: async (): Promise<Enrollment[]> => {
      return await api('/enrollments/my');
    },
  });
};

/** Обучение человека — learning.progress на него; без права бэкенд отвечает 403. */
export const useUserEnrollments = (userId: string, isEnabled = true) =>
  useQuery({
    queryKey: ['enrollments', 'user', userId],
    queryFn: async (): Promise<Enrollment[]> => api(`/enrollments/user/${userId}`),
    enabled: !!userId && isEnabled,
    retry: false,
  });

/** Кто записан на курс — только люди в охвате learning.progress смотрящего. */
export const useCourseEnrollments = (courseId: string, isEnabled = true) =>
  useQuery({
    queryKey: ['enrollments', 'course', courseId],
    queryFn: async (): Promise<CourseEnrollment[]> => api(`/enrollments/course/${courseId}`),
    enabled: !!courseId && isEnabled,
  });

export const useEnrollment = (id: string) => {
  return useQuery({
    queryKey: ['enrollments', id],
    queryFn: async (): Promise<Enrollment> => {
      return await api(`/enrollments/${id}`);
    },
    enabled: !!id,
  });
};

export const useEnroll = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (courseId: string): Promise<Enrollment> => {
      return await api('/enrollments', {
        method: 'POST',
        body: { courseId },
      });
    },
    onSuccess: () => {
      trackEvent(AnalyticsEvents.COURSE_ENROLLED);
      queryClient.invalidateQueries({ queryKey: ['enrollments'] });
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
  });
};

export const useUpdateModuleProgress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      enrollmentId,
      moduleId,
      isCompleted,
    }: {
      enrollmentId: string;
      moduleId: string;
      isCompleted: boolean;
    }): Promise<Enrollment> => {
      return await api(`/enrollments/${enrollmentId}/progress`, {
        method: 'PATCH',
        body: { moduleId, isCompleted },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'enrollments',
      });
    },
  });
};

export const useSubmitQuizAttempt = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      enrollmentId,
      answers,
    }: {
      enrollmentId: string;
      answers: { questionId: string; selectedOptions: number[] }[];
    }): Promise<Enrollment> => {
      return await api(`/enrollments/${enrollmentId}/quiz-attempt`, {
        method: 'POST',
        body: { answers },
      });
    },
    onSuccess: (enrollment) => {
      trackEvent(AnalyticsEvents.COURSE_QUIZ_SUBMITTED, {
        passed: enrollment.status === 'completed',
      });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'enrollments',
      });
    },
  });
};

/**
 * Ссылка плеера SCORM. iframe не шлёт заголовок авторизации, поэтому бэкенд выдаёт ключ
 * запуска прямо в пути. Каждое открытие плеера — свежий ключ.
 */
export const useScormLaunch = (courseId: string) => {
  return useQuery({
    queryKey: ['scorm-launch', courseId],
    queryFn: async (): Promise<{ url: string }> => {
      return await api(`/scorm/${courseId}/launch`, { method: 'POST' });
    },
    enabled: !!courseId,
    gcTime: 0,
    staleTime: Infinity,
    retry: false,
  });
};
